import React from 'react';
import { createPortal } from 'react-dom';
import { DragOverlay as DndKitDragOverlay } from '@dnd-kit/core';
import { DragDropContainerType, DragDropElementWrapper } from './index';

interface DragOverlayComponentProps<T = unknown> {
  activeItem: DragDropElementWrapper<T> | undefined;
  type: DragDropContainerType;
  iconName?: string;
  SortableComponent: React.ComponentType<{
    item: DragDropElementWrapper<T>;
    isDragOverlay?: boolean;
    iconName?: string;
  }>;
  usePortal?: boolean;
}

export const DragOverlayComponent = <T = unknown,>({
  activeItem,
  type,
  iconName,
  SortableComponent,
  usePortal = false,
}: DragOverlayComponentProps<T>) => {
  const isTable = type === DragDropContainerType.TABLE;

  const overlayContent = (
    <DndKitDragOverlay>
      {activeItem ? (
        isTable ? (
          <table className="dnd-kit-overlay-table">
            <tbody>
              <SortableComponent item={activeItem} isDragOverlay={true} iconName={iconName} />
            </tbody>
          </table>
        ) : (
          <SortableComponent item={activeItem} isDragOverlay={true} iconName={iconName} />
        )
      ) : null}
    </DndKitDragOverlay>
  );

  // If usePortal is false, render directly without portal
  if (!usePortal) {
    return overlayContent;
  }

  return (
    <>
      {/*
        IMPORTANT: We use React Portal to render DragOverlay at document.body level.
        This is crucial for nested drag-and-drop contexts because:

        1. Without portal, the overlay is constrained by parent container styles:
           - overflow: hidden/auto clips the overlay
           - transform properties affect positioning
           - z-index stacking contexts cause layering issues

        2. In our nested structure (ForgeCards containing ForgeLists), when dragging
           items from scrolled containers, the overlay would be misaligned because
           it calculates position relative to the scrolled parent, not the viewport.

        3. Portal renders the overlay outside the component hierarchy, ensuring:
           - Proper positioning relative to viewport
           - No clipping by parent overflow settings
           - Correct z-index layering above all elements
           - No transform inheritance from parent containers

        This fixes the issue where ForgeList item overlays were overflowing and
        misaligned when dragging from scrolled ForgeCard containers.
      */}
      {createPortal(overlayContent, document.body)}
    </>
  );
};
